import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../../Styles/PageStyles";
import Images from "../../../Images";
import tw from "tailwind-styled-components";
import { PaginationLimit } from "../../../Config";
import { IoCopySharp } from "react-icons/io5";
import moment from "moment";
import {
    capitalizeFirstLetter,
    handleCopy,
    truncateText,
} from "../../../helpers";
import { FiChevronRight } from "react-icons/fi";
import { MdOutlineChevronRight } from "react-icons/md";

const ClientTable = ({ apiData, setSelectedData, setShowViewProps }) => {
    const storageKey = `client_list_page`;
    const [data, setData] = useState([
        {
            deviceName: "",
            extractionTime: "",
            processingTime: "",
            drops: "",
            action: "",
        },
    ]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const storageValue = sessionStorage.getItem(storageKey);
    const savedPage = storageValue !== null ? Number(storageValue) : 0;
    const [currentPage, setCurrentPage] = useState(savedPage);
    useEffect(() => {
        setCurrentPage(savedPage);
    }, [savedPage]);

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    const showProps = (item) => {
        setSelectedData(item);
        setShowViewProps(true);
    };

    const getProperties = (item) => {
        return (
            <div className="flex items-center gap-2">
                <button
                    onClick={() => showProps(item)}
                    title="Properties"
                    className="px-3 py-1 bg-neutral-100 hover:text-primary-color flex items-center gap-2 rounded-md"
                >
                    <span className="text-gray-700">{item.propLeaseCount}</span>
                    <FiChevronRight size={14} />
                </button>
            </div>
        );
    };

    const ActionBtns = (item) => {
        return (
            <div className="flex gap-4 ">
                <button
                    onClick={() => showProps(item)}
                    className={`cursor-pointer text-sm px-4 py-1 rounded-lg ${" text-gray-600"} flex items-center gap-1`}
                >
                    <MdOutlineChevronRight className="text-gray-300" size={24} />
                </button>
            </div>
        );
    };
    useEffect(() => {
        if (apiData) {
            const tempData = apiData.map((item) => {
                const properties = item?.properties || [];
                const totalRent = Array.isArray(properties)
                    ? properties.reduce((sum, property) => {
                        return sum + Number(property?.leaseRent || 0);
                    }, 0)
                    : 0;

                return {
                    name: getName(item),
                    properties: getProperties(item),
                    rent: '₹' + totalRent,
                    security: getProperties(item),
                    collections: getCollections(item),
                    dues: getDuesAmount(item),
                    createdAt: moment(item.createdAt).format('DD, MMM YY'),
                    action: ActionBtns(item),
                };
            });
            setData(tempData);
        }
    }, [apiData, openActionMenu]);

    const getDuesAmount = (item) => {
        return <Amount>{"₹" + (item?.pendingDues || 0)}</Amount>;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest("#menuOptions") &&
                !event.target.closest(".action-btn")
            ) {
                setOpenActionMenu(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const getCollections = (item) => {
        return (
            <div className="flex flex-col gap-1">
                <span className="text-gray-600 text-sm block max-w-[160px] break-words line-clamp-2">
                    {"₹" + (item?.totalCollection || 0)}
                </span>
                <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">
                        Security: {"₹" + (item?.totalSecurityCollection || 0)}
                    </span>
                </div>
            </div>
        );
    };

    const getName = (item) => {
        const name = capitalizeFirstLetter(item?.clientName || "N/A");
        return (
            <UserImageWrapper>
                <UserImage src={Images.Avatar} alt={`Avatar for ${item.clientName}`} />
                <div className="flex flex-col gap-1">
                    <span className="text-gray-600 text-sm block max-w-[160px] break-words line-clamp-2">
                        {truncateText(name, 35)}{" "}
                    </span>
                    <div className="flex gap-1">
                        <span className="text-gray-400 text-xs">#{item.clientMobile}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(item.clientMobile);
                            }}
                        >
                            <IoCopySharp size={10} className="text-gray-400" />
                        </button>
                    </div>
                </div>
            </UserImageWrapper>
        );
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Name",
                    accessor: "name",
                },
                {
                    Header: "Properties",
                    accessor: "properties",
                },
            ];
        }
        return [
            {
                Header: "Name",
                accessor: "name",
            },
            {
                Header: "Properties",
                accessor: "properties",
            },
            {
                Header: "Total Collected",
                accessor: "collections",
            },
            {
                Header: "Monthly Rent",
                accessor: "rent",
            },
            {
                Header: "Dues",
                accessor: "dues",
            },
            {
                Header: "",
                accessor: "action",
            },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        sessionStorage.setItem(storageKey, pageIndex);
    }, [pageIndex, storageKey]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody
                    {...getTableBodyProps()}
                    hasdata={(apiData?.length > 0).toString()}
                >
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({ style: { height: "80px" } })}
                                    hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                                onClick={() => {
                                                    if (
                                                        cellIndex === 0 ||
                                                        (cellIndex === 1 && window.innerWidth < 768)
                                                    ) {
                                                        toggleRow(rowIndex);
                                                    }
                                                }}
                                            >
                                                {cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>Date</strong> {data[rowIndex].createdAt}
                                                </li>
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const CustomTable = tw.table` w-full text-gray-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) =>
    props.hasdata === "true" ? "border-b" : ""}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const UserImageWrapper = tw.div` flex items-center gap-2`;
const UserImage = tw.img`w-10 h-10 rounded-full `;
const Amount = tw.p`text-gray-600 text-sm bg-rose-100 px-3 py-1 rounded-md inline-flex cursor-pointer`;
export default ClientTable;
